/**
 * Swap Execution Hook
 *
 * State machine for Jupiter wallet swaps with Turnkey signing.
 * States: idle -> quoting -> ready -> signing -> sending -> confirming -> success/failure
 *
 * Uses Jupiter Ultra API via the jupiter-swap edge function proxy.
 */

import { useCallback, useReducer, useRef, useEffect } from 'react';
import { VersionedTransaction, type Transaction } from '@solana/web3.js';
import { supabase } from '@/integrations/supabase/client';
import { mapTradeError, formatErrorForSentry } from '@/lib/trading/errorMapper';
import { showInsufficientSolToast } from '@/lib/solana/insufficientSolGuard';
import * as Sentry from '@/lib/sentry';

// Jupiter quote response shape (from /ultra/v1/order)
export interface JupiterQuote {
  requestId: string;
  transaction: string; // base64 VersionedTransaction
  inAmount: string;
  outAmount: string;
  inputMint: string;
  outputMint: string;
  priceImpactPct?: string;
  slippageBps?: number;
  otherAmountThreshold?: string;
}

// Swap state machine states
export type SwapStatus =
  | 'idle'
  | 'quoting'
  | 'ready'
  | 'signing'
  | 'sending'
  | 'confirming'
  | 'success'
  | 'failure';

export interface SwapState {
  status: SwapStatus;
  quote: JupiterQuote | null;
  signature: string | null;
  error: string | null;
  inputAmount: string | null;
  outputAmount: string | null;
}

type SwapAction =
  | { type: 'START_QUOTE' }
  | { type: 'QUOTE_SUCCESS'; quote: JupiterQuote }
  | { type: 'QUOTE_ERROR'; error: string }
  | { type: 'START_SIGNING' }
  | { type: 'START_SENDING' }
  | { type: 'START_CONFIRMING'; signature: string }
  | { type: 'SWAP_SUCCESS'; signature: string; inputAmount: string; outputAmount: string }
  | { type: 'SWAP_FAILURE'; error: string; signature?: string }
  | { type: 'RESET' };

const initialState: SwapState = {
  status: 'idle',
  quote: null,
  signature: null,
  error: null,
  inputAmount: null,
  outputAmount: null,
};

function swapReducer(state: SwapState, action: SwapAction): SwapState {
  switch (action.type) {
    case 'START_QUOTE':
      return {
        ...state,
        status: 'quoting',
        error: null,
      };
    case 'QUOTE_SUCCESS':
      return {
        ...state,
        status: 'ready',
        quote: action.quote,
        error: null,
      };
    case 'QUOTE_ERROR':
      return {
        ...state,
        status: 'idle',
        quote: null,
        error: action.error,
      };
    case 'START_SIGNING':
      return {
        ...state,
        status: 'signing',
      };
    case 'START_SENDING':
      return {
        ...state,
        status: 'sending',
      };
    case 'START_CONFIRMING':
      return {
        ...state,
        status: 'confirming',
        signature: action.signature,
      };
    case 'SWAP_SUCCESS':
      return {
        ...state,
        status: 'success',
        signature: action.signature,
        inputAmount: action.inputAmount,
        outputAmount: action.outputAmount,
        error: null,
      };
    case 'SWAP_FAILURE':
      return {
        ...state,
        status: 'failure',
        signature: action.signature ?? state.signature,
        error: action.error,
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

export interface UseSwapExecutionProps {
  signTransaction: (tx: Transaction | VersionedTransaction) => Promise<Transaction | VersionedTransaction>;
  walletAddress: string;
  connected: boolean;
}

export interface UseSwapExecutionResult {
  state: SwapState;
  fetchQuote: (inputMint: string, outputMint: string, amount: string) => Promise<void>;
  executeSwap: () => Promise<void>;
  reset: () => void;
  isExecuting: boolean;
  canExecute: boolean;
}

export function useSwapExecution({
  signTransaction,
  walletAddress,
  connected,
}: UseSwapExecutionProps): UseSwapExecutionResult {
  const [state, dispatch] = useReducer(swapReducer, initialState);

  // Refs for cancellation
  const quoteAbortRef = useRef<AbortController | null>(null);
  const executeAbortRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      quoteAbortRef.current?.abort();
      executeAbortRef.current?.abort();
    };
  }, []);

  /**
   * Fetch a swap quote via Jupiter Ultra API
   */
  const fetchQuote = useCallback(
    async (inputMint: string, outputMint: string, amount: string) => {
      // Cancel pending quote
      quoteAbortRef.current?.abort();
      quoteAbortRef.current = new AbortController();

      if (!amount || amount === '0') {
        dispatch({ type: 'RESET' });
        return;
      }

      dispatch({ type: 'START_QUOTE' });

      const span = Sentry.startSpan({ name: 'swap.quote' });

      try {
        const { data, error } = await supabase.functions.invoke('jupiter-swap', {
          body: {
            operation: 'order',
            inputMint,
            outputMint,
            amount,
            taker: walletAddress,
          },
        });

        // Check if aborted
        if (quoteAbortRef.current?.signal.aborted) return;

        if (error) {
          throw new Error(error.message || 'Failed to fetch swap quote');
        }

        if (!data || !data.requestId || !data.transaction) {
          const errMsg = data?.error || 'Invalid quote response';
          throw new Error(errMsg);
        }

        const quote: JupiterQuote = {
          requestId: data.requestId,
          transaction: data.transaction,
          inAmount: data.inAmount,
          outAmount: data.outAmount,
          inputMint: data.inputMint || inputMint,
          outputMint: data.outputMint || outputMint,
          priceImpactPct: data.priceImpactPct,
          slippageBps: data.slippageBps,
          otherAmountThreshold: data.otherAmountThreshold,
        };

        dispatch({ type: 'QUOTE_SUCCESS', quote });

        Sentry.addBreadcrumb('swap_quote_fetched', 'wallet-swap', {
          inputMint: inputMint.slice(0, 8),
          outputMint: outputMint.slice(0, 8),
          inAmount: quote.inAmount,
          outAmount: quote.outAmount,
        });

        span?.setAttributes?.({
          'swap.input_mint': inputMint.slice(0, 8),
          'swap.output_mint': outputMint.slice(0, 8),
          'swap.in_amount': quote.inAmount,
          'swap.out_amount': quote.outAmount,
        });
      } catch (err) {
        if (quoteAbortRef.current?.signal.aborted) return;

        const mapped = mapTradeError(err);
        dispatch({ type: 'QUOTE_ERROR', error: mapped.message });

        Sentry.captureError(err instanceof Error ? err : new Error(String(err)), {
          feature: 'wallet-swap',
          action: 'quote',
        });
      } finally {
        span?.end();
      }
    },
    [walletAddress]
  );

  /**
   * Execute the swap: sign the Jupiter transaction and submit via /execute
   */
  const executeSwap = useCallback(async () => {
    if (!connected || !walletAddress) {
      dispatch({ type: 'SWAP_FAILURE', error: 'Wallet not connected' });
      return;
    }

    if (!state.quote) {
      dispatch({ type: 'SWAP_FAILURE', error: 'No quote available' });
      return;
    }

    // Cancel pending
    executeAbortRef.current?.abort();
    executeAbortRef.current = new AbortController();

    const { quote } = state;
    const signSpan = Sentry.startSpan({ name: 'swap.sign' });

    try {
      // 1. Sign the transaction
      dispatch({ type: 'START_SIGNING' });

      Sentry.addBreadcrumb('swap_signing_started', 'wallet-swap', {
        requestId: quote.requestId,
      });

      // Decode base64 transaction from Jupiter
      const txBytes = Buffer.from(quote.transaction, 'base64');
      const versionedTx = VersionedTransaction.deserialize(txBytes);

      // Sign via Turnkey (handles hex conversion internally)
      const signedTx = await signTransaction(versionedTx);

      signSpan?.end();

      if (executeAbortRef.current?.signal.aborted) return;

      // 2. Submit signed transaction to Jupiter /execute
      dispatch({ type: 'START_SENDING' });

      const sendSpan = Sentry.startSpan({ name: 'swap.execute' });

      // Serialize signed tx back to base64
      const signedBytes = (signedTx as VersionedTransaction).serialize();
      const signedBase64 = Buffer.from(signedBytes).toString('base64');

      const { data, error } = await supabase.functions.invoke('jupiter-swap', {
        body: {
          operation: 'execute',
          signedTransaction: signedBase64,
          requestId: quote.requestId,
        },
      });

      if (executeAbortRef.current?.signal.aborted) return;

      if (error) {
        throw new Error(error.message || 'Failed to submit swap');
      }

      // 3. Handle /execute response
      const signature = data?.signature || data?.txid;
      const status = data?.status;

      if (signature) {
        dispatch({ type: 'START_CONFIRMING', signature });
      }

      sendSpan?.end();

      if (status === 'Success' || status === 'Landed') {
        const inputResult = data?.inputAmountResult || quote.inAmount;
        const outputResult = data?.outputAmountResult || quote.outAmount;

        dispatch({
          type: 'SWAP_SUCCESS',
          signature: signature || '',
          inputAmount: inputResult,
          outputAmount: outputResult,
        });

        Sentry.addBreadcrumb('swap_confirmed', 'wallet-swap', {
          signature: (signature || '').slice(0, 16),
          inputAmount: inputResult,
          outputAmount: outputResult,
        });
      } else if (status === 'Failed') {
        throw new Error(data?.error || 'Swap transaction failed on-chain');
      } else {
        // Unknown status, treat as pending/success if we have a signature
        if (signature) {
          dispatch({
            type: 'SWAP_SUCCESS',
            signature,
            inputAmount: quote.inAmount,
            outputAmount: quote.outAmount,
          });
        } else {
          throw new Error('Unexpected response from swap execution');
        }
      }
    } catch (err) {
      if (executeAbortRef.current?.signal.aborted) return;

      const mapped = mapTradeError(err);
      const formatted = formatErrorForSentry(err);

      if (mapped.isInsufficientSol) {
        showInsufficientSolToast();
      }

      dispatch({
        type: 'SWAP_FAILURE',
        error: mapped.message,
      });

      Sentry.addBreadcrumb('swap_failed', 'wallet-swap', {
        error: mapped.message,
        error_code: formatted.code,
      });

      Sentry.captureError(err instanceof Error ? err : new Error(String(err)), {
        feature: 'wallet-swap',
        action: 'execute',
        error_code: formatted.code,
      });
    }
  }, [connected, walletAddress, state.quote, signTransaction]);

  /**
   * Reset state to idle
   */
  const reset = useCallback(() => {
    quoteAbortRef.current?.abort();
    executeAbortRef.current?.abort();
    dispatch({ type: 'RESET' });
  }, []);

  const isExecuting = ['signing', 'sending', 'confirming'].includes(state.status);
  const canExecute = connected && state.status === 'ready' && state.quote !== null;

  return {
    state,
    fetchQuote,
    executeSwap,
    reset,
    isExecuting,
    canExecute,
  };
}

/**
 * Hook to auto-reset success/failure state after a delay
 */
export function useSwapAutoReset(
  status: SwapStatus,
  reset: () => void,
  delayMs: number = 3000
): void {
  useEffect(() => {
    if (status === 'success') {
      const timeout = setTimeout(reset, delayMs);
      return () => clearTimeout(timeout);
    }
  }, [status, reset, delayMs]);
}
